import { useContext, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { isEmpty } from "lodash-es";
import { ChevronLeft, Plus, Save, Trash2 } from "lucide-react";

import { AppStateContext } from "@/state/AppStateContext";
import { checkAgent, deleteAgent, getAgent, getAllCustomGPTs, saveAgent } from "@/api/api";
import { CustomGPT } from "@/api/models";
import { refreshUserAgents, setTitle } from "@/utils/utils";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const EditCustomGPT = () => {
  const appStateContext = useContext(AppStateContext);
  const navigate = useNavigate();
  const { agentId } = useParams();
  const ENV = appStateContext?.state?.frontendSettings?.display_env || '';
  const CREATOR_GROUP = appStateContext?.state.frontendSettings?.creator_group || '';
  const handleRefreshUserAgents = () => refreshUserAgents(appStateContext);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isLoadingCustomGPTs, setIsLoadingCustomGPTs] = useState<boolean>(false);
  const [hasRun, setHasRun] = useState<boolean>(false);
  const [searchText, setSearchText] = useState<string>('');
  const [customGPTs, setCustomGPTs] = useState<CustomGPT[]>([]);
  const [agentID, setAgentID] = useState<string>('');
  const [selectedCustomGPTIDs, setSelectedCustomGPTIDs] = useState<string[]>([]);
  const [type, setType] = useState<string>('cgpt_selector');
  const [name, setName] = useState<string>('');
  const [description, setDescription] = useState<string>('');
  const [errors, setErrors] = useState<{ name?: string; description?: string; customGPTs?: string; }>({});
  const [isSaving, setIsSaving] = useState<boolean>(false);
  const [isChecking, setIsChecking] = useState<boolean>(false);
  const [similar, setSimilar] = useState<any>([]);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);

  /** 
   * Handle checking user has access to this page
  **/
  useEffect(() => {
    if ((!appStateContext?.state.userGroups.includes(CREATOR_GROUP) && !isEmpty(agentId) && ENV !== 'DEV')) navigate('/agents');
  }, [appStateContext?.state.userGroups, agentId]);

  /** 
   * Handle loading Custom GPTs
  **/
  useEffect(() => {
    loadCustomGPTs();
    setHasRun(true);
  }, []);

  /** 
   * Handle loading agent
  **/
  useEffect(() => {
    if (isEmpty(agentId)) setTitle(appStateContext, 'New Agent');
    if (!isEmpty(appStateContext?.state.currentUser) && !isEmpty(agentId)) loadAgent();
  }, [appStateContext?.state.currentUser, agentId]);

  /** 
   * Debounce search input (delay rate at which event fires and performs API call to query Custom GPTs)
  **/
  useEffect(() => {
    if (hasRun) {
      const h = setTimeout(() => { loadCustomGPTs(); }, 300);
      return () => clearTimeout(h);
    }
  }, [searchText]);
  
  /** 
   * Handle loading all Custom GPTs
  **/
  const loadCustomGPTs = async () => {
    // Toggle loading state
    setIsLoadingCustomGPTs(true);

    // Get data
    const res = await getAllCustomGPTs(searchText, 0);
    
    // Set state
    setCustomGPTs(res);

    // Toggle loading state
    setIsLoadingCustomGPTs(false);
  }

  /** 
   * Handle validating that the required form fields are filled
  **/
  const validateInputs = () => {
    const lErrors: { name?: string; description?: string; customGPTs?: string; } = {};
    if (name.trim() === '') lErrors.name = 'Name is required';
    if (description.trim() === '') lErrors.description = 'Description is required';
    if (selectedCustomGPTIDs.length === 0) lErrors.customGPTs = 'At least 1 Space must be selected';
    setErrors(lErrors);
    return lErrors;
  }

  /** 
   * Handle getting details for the selected agent
  **/
  const loadAgent = () => {
    setIsLoading(true);
    getAgent(agentId || '').then((res) => {
      // If agent found
      if (res && res['agent'] && res['agent'].length > 0) {
        let agent = res['agent'][0];

        // Set document title
        setTitle(appStateContext, agent.name);

        // Confirm agent belongs to current user, if not redirect away
        if (agent.createdBy != appStateContext?.state.currentUser?.id) navigate('/agents');

        // Set values
        setAgentID(agent.id);
        setType(agent.type || '');
        setName(agent.name);
        setDescription(agent.description);
        setSelectedCustomGPTIDs(agent.customGPTs.split('|'));
      }
      // Otherwise, navigate away
      else navigate('/agents');
      
      setIsLoading(false);
    }).catch((err) => {
      navigate('/agents');
    });
  }

  /** 
   * Handle saving Custom GPT
  **/
  const handleSave = async (force: boolean = false) => {
    // Check all required fields are filled
    const lErrors = validateInputs();
    
    // If all required fields are filled, create Custom GPT
    if (Object.keys(lErrors).length === 0) {
      // Set loading variable
      setIsSaving(true);
      setIsChecking(false);

      // If creating, check for similar agents
      if (!force && isEmpty(agentID)) {
        setIsChecking(true);
        const similar = await checkAgent(selectedCustomGPTIDs.join('|'));

        if (similar && similar.length > 0) {
          setSimilar(similar);
          return;
        }
        else setIsChecking(false);
      }

      // Create FormData object and append all fields to it
      const formData = new FormData();
      formData.append('id', agentID);
      formData.append('type', type);
      formData.append('name', name.trim());
      formData.append('description', description.trim());
      formData.append('customGPTs', selectedCustomGPTIDs.join('|'));

      // Save agent
      await saveAgent(formData);

      // Reload user's agents
      handleRefreshUserAgents();

      // Redirect to agents home
      navigate('/agents');
    }
  }

  /** 
   * Handle deleting an agent
  **/
  const handleDelete = async () => {
    // Confirm user wants to delete this agent
    if (agentId && !isEmpty(agentId) && confirm('Are you sure you want to delete this agent?')) {
      setIsDeleting(true);
      await deleteAgent(agentId);
      handleRefreshUserAgents();
      setIsDeleting(false);
      navigate('/agents');
    }
  }

  /** 
   * Handle toggling CustomGPT selection
   * 
   * @param {string} customGPTID - The ID of the CustomGPT to toggle
  **/
  const toggleCustomGPTSelection = (customGPTID: string) => {
    setSelectedCustomGPTIDs(prev => {
      if (prev.includes(customGPTID)) {
        return prev.filter(id => id !== customGPTID);
      } else {
        return [...prev, customGPTID];
      }
    });
  }

  /** 
   * Helper function to capitalize the first letter of first two words of Custom GPT name, if it does not have an icon image
   * 
   * @param {string} inputString - The Custom GPT's name
  **/
  const lettersAsIcon = (inputString: string) => {
    const words = inputString.split(' ');
    const initials = [];

    if (words.length >= 1) initials.push(words[0].charAt(0).toUpperCase());
    if (words.length >= 2) initials.push(words[1].charAt(0).toUpperCase());

    return initials.join('');
  }

  return (
    <div className="content flex flex-col h-full bg-base-100">
      {isLoading ? 
        <div className="flex items-center justify-center h-full">
          <div className="loading-icon w-24 h-24"></div>
        </div>
      :
        <>
          <div className="flex items-center justify-between border-b border-accent ps-2 pe-4 pb-2 mt-2">
            <div className="flex items-center gap-4">
              <button type="button" className="btn btn-ghost p-0" onClick={() => navigate('/agents')}><ChevronLeft size={24} /></button>
              <div className="dark:text-white font-bold">{name || 'New Agent'}</div>
            </div>
            <div className="flex items-center gap-6">
              <button type="button" className="btn btn-primary font-bold" onClick={() => handleSave(false)}>{isEmpty(agentId) ? <><Plus className="mt-0.5" size={20} /> Create</> : <><Save size={20} /> Update</>}</button>
              {!isEmpty(agentId) ? <button type="button" onClick={handleDelete}><Trash2 className="text-error" size={20} /></button> : null}
            </div>
          </div>

          <div className="w-full md:h-screen md:flex md:flex-row overflow-y-auto scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
            {/* Left Column */}
            <div className="md:flex-1 md:h-full md:overflow-y-auto border-b md:border-b-0 md:border-e border-accent scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
              <div className="w-full p-4">
                <div className="form-control w-full mb-2">
                  <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Type</span></div>
                  <Select onValueChange={setType} value={type}>
                    <SelectTrigger className="select-bordered border-secondary bg-neutral shadow-sm">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-secondary border-accent !ring-transparent">
                      <SelectItem value="cgpt_selector">Spaces Selector</SelectItem>
                      <SelectItem value="-1" disabled>More Coming Soon</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <div className="form-control w-full mb-4">
                  <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Name</span></div>
                  <input type="text" className={`input input-sm input-bordered border-secondary bg-neutral ${Object.keys(errors).indexOf('name') > -1 ? `!border-error` : ``} focus-within:outline-none shadow-sm dark:text-white w-full`}  placeholder="Name this Agent" autoComplete="off" maxLength={100} value={name} onChange={(e) => setName(e.target.value)} />
                </div>
                
                <div className="form-control w-full mb-4">
                  <div className="label pb-1"><span className="label-text dark:text-white text-sm font-bold">Description</span></div>
                  <input type="text" className={`input input-sm input-bordered border-secondary bg-neutral ${Object.keys(errors).indexOf('description') > -1 ? `!border-error` : ``} focus-within:outline-none shadow-sm dark:text-white`} placeholder="Describe what this agent does" autoComplete="off" maxLength={5000} value={description} onChange={(e) => setDescription(e.target.value)} />
                </div>
              </div>
            </div>

            {/* Right Column */}
            <div className="md:flex-1 md:h-full md:overflow-y-auto border-b md:border-b-0 md:border-e border-secondary scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent">
              <div className="w-full p-4">
                <div className="dark:text-white text-xl font-bold mb-1">Spaces</div>
                <div className="text-black/50 dark:text-accent text-sm mb-4">Select the Spaces for this Agent</div>

                <div className="w-full my-3">
                  <label className="input input-sm input-bordered border-secondary bg-neutral focus-within:outline-none flex items-center gap-2">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 16 16"
                      fill="currentColor"
                      className="h-4 w-4 opacity-70 z-0">
                      <path
                        fillRule="evenodd"
                        d="M9.965 11.026a5 5 0 1 1 1.06-1.06l2.755 2.754a.75.75 0 1 1-1.06 1.06l-2.755-2.754ZM10.5 7a3.5 3.5 0 1 1-7 0 3.5 3.5 0 0 1 7 0Z"
                        clipRule="evenodd" />
                    </svg>
                    <input type="text" className="grow" placeholder="Search Spaces" autoComplete="off" defaultValue={searchText} onChange={(e) => setSearchText(e.target.value)} />
                  </label>
                </div>

                {Object.keys(errors).indexOf('customGPTs') > -1 ? <div className="bg-error bg-opacity-25 border border-error border-opacity-50 rounded-lg text-sm text-white py-1 px-2 mt-4">{errors.customGPTs}</div> : null}

                <div>
                  {isLoadingCustomGPTs ? 
                    <div className="flex items-center justify-center h-full">
                      <span className="loading loading-ring text-primary w-16"></span>
                    </div>
                  : null}
                  {customGPTs.map((cgpt: CustomGPT, idx: number) => (
                    <div key={idx} className="flex items-center border border-secondary rounded-xl gap-2 p-2 mb-2 cursor-pointer hover:bg-secondary" onClick={() => toggleCustomGPTSelection(cgpt.id)}>
                      <input 
                        type="checkbox" 
                        className="checkbox checkbox-sm checkbox-primary pointer-events-none" 
                        checked={selectedCustomGPTIDs.includes(cgpt.id)}
                        readOnly
                      />
                      <div className="flex flex-col shrink-0 justify-center w-10">
                        {!isEmpty(cgpt.image_path) ?
                          <img src={`https://asappsmsstore.blob.core.windows.net/customgpticons/${cgpt.image_path}?v=${cgpt.updated_at || Date.now()}`} className="rounded-xl h-10 w-10 object-cover" alt={cgpt.name} />
                        :
                          <div className="avatar static placeholder">
                            <div className="h-10 w-10 rounded-xl">
                              <span className="text-lg">{lettersAsIcon(cgpt.name)}</span>
                            </div>
                          </div>
                        }
                      </div>
                      <div>
                        <div className="font-bold">{cgpt.name || ''}</div>
                        <div className="text-[11px]">{cgpt.description.substring(0, 500) || ''}</div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* DUPLICATES CHECK / SAVING */}
          <Dialog open={isSaving} onOpenChange={setIsSaving}>
            <DialogContent className={`flex flex-col bg-secondary border-transparent rounded-3xl shadow-short [&>button:not(.btn)]:hidden max-h-[calc(100vh-4rem)] ${(!isEmpty(similar) ? `max-w-full w-full lg:w-3/4` : ``)} p-2 pe-0`} onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className={`flex items-center justify-center gap-4 pt-5 ${(!isEmpty(similar) ? `hidden` : ``)}`}>{isChecking ? <><span className="loading loading-spinner loading-md"></span> Checking...</> : <><span className="loading loading-spinner loading-md"></span> Saving...</>}</DialogTitle>
              </DialogHeader>

              <div className="max-h-[90vh] overflow-x-hidden scroll-on-hover scrollbar scrollbar-gutter-stable scrollbar-thumb-accent scrollbar-track-transparent scrollbar-w-8 px-4 mb-2">
                {!isEmpty(similar) ? <>
                  <h6><strong>Possible Duplicates Found</strong></h6>
                  <div className="text-sm mt-2">Please check out the following agents which may have the same purpose as yours. If so, talk to the owner and discuss possibilities of combining the tasks.</div>
                  <div className="flex flex-col gap-2 my-4">
                    {similar.map((s: any, idx: number) => (
                      <div key={idx} className="bg-base-100 rounded-3xl py-2 px-4">
                        <span className="text-primary underline cursor-pointer" onClick={() => window.open(`/agents/v/${s.id}`, '_blank')}><strong>{s.name}</strong></span>
                        <br/>
                        <span className="text-xs">{s.description}</span>
                        <br/>
                        <span className="text-xs">Created By: <strong>{s.createdByName}</strong></span>
                      </div>
                    ))}
                  </div>
                  <div className="flex justify-between">
                    <button type="button" className="btn btn-error" onClick={() => { setSimilar([]); setIsSaving(false); }}>Cancel</button>
                    <button type="button" className="btn btn-warning" onClick={() => { setSimilar([]); handleSave(true); }}>Create Anyways</button>
                  </div>
                </> : null}
              </div>
            </DialogContent>
          </Dialog>

          {/* DELETING */}
          <Dialog open={isDeleting} onOpenChange={setIsDeleting}>
            <DialogContent className="bg-secondary border-transparent rounded-3xl shadow-short [&>button:not(.btn)]:hidden" onInteractOutside={(e) => e.preventDefault()} onEscapeKeyDown={(e) => e.preventDefault()} aria-describedby={undefined}>
              <DialogHeader>
                <DialogTitle className="flex items-center justify-center gap-4">{isDeleting ? <div className="flex items-center text-error gap-4"><span className="loading loading-spinner loading-md text-error"></span> Deleting...</div> : 'Deleted'}</DialogTitle>
              </DialogHeader>
            </DialogContent>
          </Dialog>
        </>
      }
    </div>
  );
}

export default EditCustomGPT;
